import { useEffect, useRef, useState } from "react";
import {
  getUserGuideLink,
  openUserGuideHere
} from "../services/userGuideService";

const TITLE = "Orbit User Guide";

/**
 * Orbit's /user-guide page, which "User Guide" in the left menu opens in a new tab. It asks the backend for a
 * guide-only link and shows the guide there in a frame that fills the tab, so the address bar keeps Orbit's own
 * address. The tab's title names the guide's release. Reloading the tab gets a fresh link, and
 * /user-guide#<section> opens that part of the guide (the guide's own section links name it). If the backend
 * doesn't let this address frame the guide, the browser would show an empty error frame, so the tab opens the
 * guide directly instead.
 */
export default function UserGuidePage() {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const frame = useRef(null);

  useEffect(() => {
    let active = true;
    const before = document.title;
    document.title = TITLE;
    getUserGuideLink().then(
      (link) => {
        if (!active) return;
        const section = window.location.hash; // /user-guide#<section> opens that part of the guide
        if (link.frameOrigin !== window.location.origin) {
          openUserGuideHere(link.url + section);
          return;
        }
        document.title = link.version ? `${TITLE} ${link.version}` : TITLE;
        setUrl(link.url + section);
      },
      (error) => {
        // after a 401 the API client has already sent this tab to the sign-in page
        if (active && error?.response?.status !== 401) setFailed(true);
      }
    );
    return () => {
      active = false;
      document.title = before;
    };
  }, []);

  if (url) {
    return (
      <iframe
        ref={frame}
        src={url}
        title={TITLE}
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        className="fixed inset-0 w-full h-full border-0 bg-white"
        onLoad={() => frame.current?.focus()} // so the keyboard scrolls the guide at once
      />
    );
  }
  return (
    <p role="status" className="p-6 text-gray-700">
      {failed
        ? "Couldn't open the Orbit User Guide. Reload this page to try again."
        : "Opening the Orbit User Guide…"}
    </p>
  );
}
